import { default as React, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import AppsIcon from '@material-ui/icons/Apps';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useLocation } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import skyStageIcon from '../../../static/skyStageIcon.svg';

/**
 * Full-page App Unavailable experience shown when a microapp's pod is in an
 * error state (CrashLoopBackOff, OOMKilled, pending, etc.) and the
 * oauth2-proxy upstream fails with a 5xx error.
 *
 * This page deliberately avoids all authentication or permission language —
 * the user has valid access; the problem is the pod, not their credentials.
 *
 * Query params:
 *   ?app=<entityName>    — used to look up the catalog entity for display name
 *   ?ns=<namespace>      — catalog namespace (defaults to "default")
 */
export const AppUnavailablePage = () => {
  const location = useLocation();
  const catalogApi = useApi(catalogApiRef);

  const params = new URLSearchParams(location.search);
  const appNameParam = params.get('app') || '';
  const nsParam = params.get('ns') || 'default';

  const [displayName, setDisplayName] = useState(appNameParam);
  const [catalogUrl, setCatalogUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!appNameParam);

  useEffect(() => {
    if (!appNameParam) {
      setLoading(false);
      return;
    }

    catalogApi
      .getEntityByRef(`component:${nsParam}/${appNameParam}`)
      .then(entity => {
        if (!entity) return;
        setDisplayName(entity.metadata.title || entity.metadata.name);
      })
      .catch(() => {
        // Catalog lookup failed — keep appNameParam as display name
      })
      .finally(() => {
        // Build catalog link regardless of whether lookup succeeded
        setCatalogUrl(`/catalog/${nsParam}/component/${appNameParam}`);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appNameParam, nsParam, catalogApi]);

  return (
    // Full-viewport overlay — covers the Backstage sidebar too
    <Box
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        background:
          'linear-gradient(135deg, #0a3a3a 0%, #0d5252 45%, #1a7575 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 16px 32px',
        boxSizing: 'border-box',
        overflowY: 'auto',
      }}
    >
      {/* ── SkyStage logo above card ── */}
      <Box
        display="flex"
        alignItems="center"
        style={{ marginBottom: 32, gap: 12 }}
      >
        <img
          src={skyStageIcon}
          alt=""
          aria-hidden="true"
          style={{ height: 44 }}
        />
        <Typography
          style={{
            color: '#ffffff',
            fontSize: 30,
            fontWeight: 700,
            letterSpacing: 0.5,
            lineHeight: 1,
          }}
        >
          <span style={{ fontWeight: 300 }}>Sky</span>Stage
        </Typography>
      </Box>

      {/* ── Main card ── */}
      <Paper
        elevation={6}
        style={{
          padding: '40px 40px 32px',
          maxWidth: 520,
          width: '100%',
          borderRadius: 16,
          textAlign: 'center',
        }}
      >
        {/* Warning icon in amber circle */}
        <Box
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            backgroundColor: '#fff8e1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
          }}
          aria-hidden="true"
        >
          <WarningIcon style={{ fontSize: 36, color: '#f57c00' }} />
        </Box>

        <Typography
          variant="h5"
          style={{ fontWeight: 700, marginBottom: 12 }}
          role="heading"
          aria-level={1}
        >
          App Unavailable
        </Typography>

        <Typography variant="body1" style={{ marginBottom: 8 }}>
          {displayName || 'This application'} is temporarily unavailable.
        </Typography>

        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginBottom: 8 }}
        >
          The application is experiencing an issue and cannot be reached right
          now. Your access is valid — this is a service disruption, not an
          access issue.
        </Typography>

        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginBottom: 28 }}
        >
          Please try again later or check the app's status page in SkyStage.
        </Typography>

        {/* ── Loading while resolving catalog link ── */}
        {loading && (
          <Box display="flex" justifyContent="center" mb={3}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* ── Action buttons ── */}
        <Box
          display="flex"
          flexDirection="column"
          style={{ gap: 12 }}
          alignItems="center"
        >
          {!loading && catalogUrl && (
            <Button
              variant="contained"
              href={catalogUrl}
              startIcon={<OpenInNewIcon />}
              style={{
                backgroundColor: '#1a5f5f',
                color: '#fff',
                fontWeight: 700,
                letterSpacing: 1.5,
                padding: '10px 28px',
                borderRadius: 8,
                textTransform: 'uppercase',
                width: '100%',
                maxWidth: 340,
              }}
            >
              Check App Status in SkyStage
            </Button>
          )}

          <Button
            variant="outlined"
            href="/apps"
            startIcon={<AppsIcon />}
            style={{
              fontWeight: 700,
              letterSpacing: 1.5,
              padding: '10px 28px',
              borderRadius: 8,
              textTransform: 'uppercase',
              width: '100%',
              maxWidth: 340,
            }}
          >
            Browse All Apps
          </Button>
        </Box>
      </Paper>

      {/* ── Footer ── */}
      <Typography
        variant="caption"
        style={{
          marginTop: 28,
          color: 'rgba(255, 255, 255, 0.55)',
          textAlign: 'center',
        }}
      >
        Powered by SkyStage — WellSky's Internal Developer Portal
      </Typography>
    </Box>
  );
};
